import React, { useState, useRef } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, DatePicker, Popconfirm, message, Select } from 'antd';
import dayjs from 'dayjs';

export interface DraftAccessory {
  id?: number;
  name: string;
  quantity: number;
  unit?: string;
  price?: number;
  total_price?: number;
  purchase_date?: string;
  purchase_document?: string;
  notes?: string;
}

interface AssetAccessoriesDraftProps {
  onChange?: (list: DraftAccessory[]) => void;
}

const AssetAccessoriesDraft: React.FC<AssetAccessoriesDraftProps> = ({ onChange }) => {
  const [list, setList] = useState<DraftAccessory[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<DraftAccessory | null>(null);
  const [form] = Form.useForm();
  const formKey = useRef(0);

  const handleAdd = () => {
    setEditing(null);
    formKey.current += 1;
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: DraftAccessory, idx: number) => {
    setEditing({ ...record, id: idx });
    formKey.current += 1;
    form.resetFields();
    // Set form values after reset
    setTimeout(() => {
      form.setFieldsValue({ 
        ...record, 
        purchase_date: record.purchase_date ? dayjs(record.purchase_date) : undefined 
      });
    }, 0);
    setModalVisible(true);
  };

  const handleDelete = (idx: number) => {
    const newList = list.filter((_, i) => i !== idx);
    setList(newList);
    onChange?.(newList);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload: DraftAccessory = {
        ...values,
        purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : undefined,
        total_price: values.price && values.quantity ? values.price * values.quantity : undefined,
      };
      let newList;
      if (editing && typeof editing.id === 'number') {
        newList = list.map((item, idx) => idx === editing.id ? payload : item);
        message.success('Cập nhật vật tư/thiết bị thành công!');
      } else {
        newList = [...list, payload];
        message.success('Thêm vật tư/thiết bị thành công!');
      }
      setList(newList);
      onChange?.(newList);
      setModalVisible(false);
      form.resetFields();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
      }
      message.error('Vui lòng kiểm tra lại dữ liệu!');
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  return (
    <div>
      <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16 }}>Thêm vật tư/thiết bị</Button>
      <Table
        dataSource={list}
        rowKey={(_, idx) => String(idx)}
        pagination={false}
        style={{ marginBottom: 0 }}
        columns={[
          { title: 'Tên vật tư/thiết bị', dataIndex: 'name', key: 'name' },
          { title: 'Số lượng', dataIndex: 'quantity', key: 'quantity' },
          { title: 'Đơn vị', dataIndex: 'unit', key: 'unit' },
          { title: 'Giá', dataIndex: 'price', key: 'price', render: (v: number) => v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' VNĐ' : '—' },
{ title: 'Tổng giá', dataIndex: 'total_price', key: 'total_price', render: (v: number) => v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' VNĐ' : '—' },
          { title: 'Ngày mua', dataIndex: 'purchase_date', key: 'purchase_date', render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
          { title: 'Hồ sơ mua', dataIndex: 'purchase_document', key: 'purchase_document' },
          { title: 'Ghi chú', dataIndex: 'notes', key: 'notes' },
          {
            title: 'Thao tác',
            key: 'action',
            render: (_: any, __: any, idx: number) => (
              <>
                <Button size="small" onClick={() => handleEdit(list[idx], idx)} style={{ marginRight: 8 }}>Sửa</Button>
                <Popconfirm title="Xác nhận xóa?" onConfirm={() => handleDelete(idx)} okText="Xóa" cancelText="Hủy">
                  <Button size="small" danger>Xóa</Button>
                </Popconfirm>
              </>
            )
          }
        ]}
      />
      <Modal
        open={modalVisible}
        title={editing ? 'Cập nhật vật tư/thiết bị' : 'Thêm vật tư/thiết bị'}
        onCancel={handleCancel}
        onOk={handleOk}
        destroyOnHidden
        key={formKey.current}
      >
        <Form 
          form={form} 
          layout="vertical"
          key={formKey.current}
        >
          <Form.Item 
            name="name" 
            label="Tên vật tư/thiết bị" 
            rules={[{ required: true, message: 'Nhập tên vật tư/thiết bị' }]}
          > 
            <Input /> 
          </Form.Item>
          <Form.Item 
            name="quantity" 
            label="Số lượng" 
            rules={[{ required: true, message: 'Nhập số lượng' }]}
          > 
            <InputNumber min={1} style={{ width: '100%' }} /> 
          </Form.Item>
          <Form.Item name="unit" label="Đơn vị"> 
            <Select
              showSearch
              allowClear
              placeholder="Chọn đơn vị hoặc nhập tay"
              options={[
                { value: 'cái', label: 'cái' },
                { value: 'hộp', label: 'hộp' },
                { value: 'bộ', label: 'bộ' },
                { value: 'chiếc', label: 'chiếc' },
                { value: 'mét', label: 'mét' },
                { value: 'kg', label: 'kg' },
                { value: 'lít', label: 'lít' },
                { value: 'cuộn', label: 'cuộn' },
                { value: 'tấm', label: 'tấm' },
                { value: 'thùng', label: 'thùng' }
              ]}
              filterOption={(input: string, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="price" label="Giá từng cái"> 
            <InputNumber 
              min={0} 
              style={{ width: '100%' }}
              formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
              parser={(value: any) => value ? value.replace(/\D/g, '') : undefined}
            /> 
          </Form.Item>
          <Form.Item name="purchase_date" label="Ngày mua"> 
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /> 
          </Form.Item>
          <Form.Item name="purchase_document" label="Hồ sơ mua"> 
            <Input /> 
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú"> 
            <Input.TextArea rows={2} /> 
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetAccessoriesDraft; 